import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard, 
  FileText, 
  AlertTriangle, 
  History,
  LogOut,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  TrendingUp,
  Building,
  Monitor,
  MapPin,
  Store,
  Gift,
  BarChart3,
  DollarSign,
  Briefcase,
  Shield,
  Activity
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { ParkWiseLogo } from "../../components/ui/parkwise-logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryNames = {
  disabled_blue_badge: "Blue Badge",
  adult_with_child: "Adult + Child",
  extra_wide: "Extra Wide",
  nhs_staff: "NHS Staff",
  military: "Military",
  lone_female: "Legacy Category"
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [stats, setStats] = useState(null);
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      fetchSites();
      return;
    }

    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [selectedSite, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      fetchSites();
    } catch (error) {
      navigate("/admin/login");
    }
  };

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API}/sites`, { withCredentials: true });
      setSites(response.data);
    } catch (error) {
      console.error("Failed to fetch sites:", error);
    }
  };

  const fetchStats = async () => {
    try {
      const siteParam = selectedSite !== "all" ? `?site_id=${selectedSite}` : "";
      const response = await axios.get(`${API}/dashboard/stats${siteParam}`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {}
    navigate("/admin/login");
  };

  const getSelectedSiteName = () => {
    if (selectedSite === "all") return "All Sites";
    const site = sites.find(s => s.site_id === selectedSite);
    return site ? site.name : selectedSite;
  };

  if (loading) {
    return (
      <div className="admin-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="admin-container min-h-screen" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 border-r border-slate-800 p-6">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-600 to-green-500 flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            ParkWise
          </span>
        </div>

        {/* Site Selector */}
        <div className="mb-6">
          <label className="text-xs text-slate-500 uppercase tracking-wider mb-2 block">Site Filter</label>
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-full bg-slate-800 border-slate-700 text-white" data-testid="site-selector">
              <MapPin className="w-4 h-4 mr-2 text-slate-400" />
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-700">
              <SelectItem value="all" className="text-white">All Sites</SelectItem>
              {sites.map(site => (
                <SelectItem key={site.site_id} value={site.site_id} className="text-white">
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <nav className="space-y-2">
          <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-800 text-white" data-testid="nav-dashboard">
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link to="/admin/registrations" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-registrations">
            <FileText className="w-5 h-5" />
            <span>Registrations</span>
          </Link>
          <Link to="/admin/sites" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-sites">
            <Building className="w-5 h-5" />
            <span>Sites & Kiosks</span>
          </Link>
          <Link to="/admin/retailers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-retailers">
            <Store className="w-5 h-5" />
            <span>Retailers</span>
          </Link>
          <Link to="/admin/rewards" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-rewards">
            <Gift className="w-5 h-5" />
            <span>Rewards</span>
          </Link>
          <Link to="/admin/analytics" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-analytics">
            <BarChart3 className="w-5 h-5" />
            <span>Analytics</span>
          </Link>
          
          {/* Barrier Operations Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Barrier Operations</p>
            <Link to="/admin/freeflow" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-freeflow">
              <Shield className="w-5 h-5" />
              <span>Freeflow Whitelist</span>
            </Link>
            <Link to="/admin/exit-auths" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-exit-auths">
              <Clock className="w-5 h-5" />
              <span>Exit Authorisations</span>
            </Link>
            <Link to="/admin/barrier" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-barrier">
              <Activity className="w-5 h-5" />
              <span>Barrier Control</span>
            </Link>
          </div>
          
          {/* Revenue Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Revenue</p>
            <Link to="/admin/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-advertisers">
              <Briefcase className="w-5 h-5" />
              <span>Advertisers</span>
            </Link>
            <Link to="/admin/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-campaigns">
              <BarChart3 className="w-5 h-5" />
              <span>Campaigns</span>
            </Link>
            <Link to="/admin/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-billing">
              <DollarSign className="w-5 h-5" />
              <span>Billing</span>
            </Link>
            <Link to="/admin/revenue" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-revenue">
              <TrendingUp className="w-5 h-5" />
              <span>Revenue Dashboard</span>
            </Link>
          </div>
          
          {/* Signage CMS Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Signage CMS</p>
            <Link to="/admin/screens" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-screens">
              <Monitor className="w-5 h-5" />
              <span>Screens</span>
            </Link>
            <Link to="/admin/content" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-content">
              <FileText className="w-5 h-5" />
              <span>Content</span>
            </Link>
            <Link to="/admin/playlists" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-playlists">
              <LayoutDashboard className="w-5 h-5" />
              <span>Playlists</span>
            </Link>
          </div>
          
          {/* Advertising Module Section */}
          <div className="pt-4 mt-4 border-t border-slate-700">
            <p className="text-xs text-slate-500 uppercase tracking-wider px-4 mb-2">Advertising</p>
            <Link to="/admin/advertising" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-ad-dashboard">
              <LayoutDashboard className="w-5 h-5" />
              <span>Ad Dashboard</span>
            </Link>
            <Link to="/admin/advertising/advertisers" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-ad-clients">
              <Users className="w-5 h-5" />
              <span>Ad Clients</span>
            </Link>
            <Link to="/admin/advertising/campaigns" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-ad-campaigns">
              <BarChart3 className="w-5 h-5" />
              <span>Ad Campaigns</span>
            </Link>
          </div>
          
          <Link to="/admin/ocr-exceptions" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-ocr-exceptions">
            <AlertTriangle className="w-5 h-5" />
            <span>OCR Exceptions</span>
            {stats?.ocr_exceptions > 0 && (
              <span className="ml-auto bg-orange-500 text-white text-xs px-2 py-1 rounded-full">
                {stats.ocr_exceptions}
              </span>
            )}
          </Link>
          <Link to="/admin/audit-log" className="flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-testid="nav-audit-log">
            <History className="w-5 h-5" />
            <span>Audit Log</span>
          </Link>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <div className="flex items-center gap-3 mb-4">
            <ParkWiseLogo size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm w-full" data-testid="logout-button">
            <LogOut className="w-4 h-4" />
            <span>Sign out</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="ml-64 p-8">
        <div className="mb-8">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            <span>{getSelectedSiteName()}</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-slate-400">
            {selectedSite === "all" ? "Overview across all sites" : `Metrics for ${getSelectedSiteName()}`}
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card 
            className="admin-metric-card border-slate-800 cursor-pointer hover:border-emerald-500/50 transition-colors"
            onClick={() => navigate('/admin/registrations?status=active')}
            data-testid="kpi-total-active"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Total Active</CardTitle>
              <CheckCircle className="w-5 h-5 text-emerald-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.total_active || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Whitelist records</p>
            </CardContent>
          </Card>

          <Card 
            className="admin-metric-card border-slate-800 cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => navigate('/admin/registrations?expiring=soon')}
            data-testid="kpi-expiring-soon"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">Expiring Soon</CardTitle>
              <Clock className="w-5 h-5 text-amber-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.expiring_soon || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Within 30 days</p>
            </CardContent>
          </Card>

          <Card 
            className="admin-metric-card border-slate-800 cursor-pointer hover:border-orange-500/50 transition-colors"
            onClick={() => navigate('/admin/ocr-exceptions')}
            data-testid="kpi-ocr-exceptions"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">OCR Exceptions</CardTitle>
              <AlertTriangle className="w-5 h-5 text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">{stats?.ocr_exceptions || 0}</div>
              <p className="text-xs text-slate-500 mt-1">Pending review</p>
            </CardContent>
          </Card>

          <Card 
            className="admin-metric-card border-slate-800 cursor-pointer hover:border-blue-500/50 transition-colors"
            onClick={() => navigate('/admin/sites')}
            data-testid="kpi-sites"
          >
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-slate-400">
                {selectedSite === "all" ? "Sites" : "Kiosks"}
              </CardTitle>
              {selectedSite === "all" ? (
                <Building className="w-5 h-5 text-blue-400" />
              ) : (
                <Monitor className="w-5 h-5 text-blue-400" />
              )}
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-white">
                {selectedSite === "all" ? stats?.total_sites || 0 : stats?.total_kiosks || 0}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {selectedSite === "all" ? "Active locations" : "At this site"}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Registrations by Category */}
          <Card className="admin-card border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">Registrations by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {stats?.by_category && Object.entries(stats.by_category)
                  .filter(([category]) => category !== 'lone_female')
                  .map(([category, count]) => (
                  <div 
                    key={category} 
                    className="flex items-center justify-between cursor-pointer hover:bg-slate-800/50 rounded-lg p-2 -mx-2 transition-colors"
                    onClick={() => navigate(`/admin/registrations?category=${category}`)}
                    data-testid={`category-${category}`}
                  >
                    <span className="text-slate-300">{categoryNames[category] || category}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-blue-600 to-green-500 rounded-full"
                          style={{ width: `${Math.min((count / (stats.total_active || 1)) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-white font-medium w-8 text-right">{count}</span>
                    </div>
                  </div>
                ))}
                {(!stats?.by_category || Object.keys(stats.by_category).filter(c => c !== 'lone_female').length === 0) && (
                  <p className="text-slate-500 text-center py-4">No registrations yet</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Site Distribution or Status Overview */}
          <Card className="admin-card border-slate-800">
            <CardHeader>
              <CardTitle className="text-lg text-white">
                {selectedSite === "all" ? "By Site" : "Status Overview"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedSite === "all" && stats?.by_site ? (
                <div className="space-y-4">
                  {Object.entries(stats.by_site).map(([siteId, data]) => (
                    <div 
                      key={siteId} 
                      className="flex items-center justify-between cursor-pointer hover:bg-slate-800/50 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/admin/registrations?site_id=${siteId}`)}
                      data-testid={`site-${siteId}`}
                    >
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span className="text-slate-300">{data.name || siteId}</span>
                      </div>
                      <span className="text-white font-medium">{data.count}</span>
                    </div>
                  ))}
                  {Object.keys(stats.by_site).length === 0 && (
                    <p className="text-slate-500 text-center py-4">No site data available</p>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {stats?.by_status && Object.entries(stats.by_status).map(([status, count]) => (
                    <div 
                      key={status} 
                      className="flex items-center justify-between cursor-pointer hover:bg-slate-800/50 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/admin/registrations?status=${status}`)}
                      data-testid={`status-${status}`}
                    >
                      <div className="flex items-center gap-2">
                        {status === "active" && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                        {status === "expired" && <Clock className="w-4 h-4 text-amber-400" />}
                        {status === "suspended" && <AlertTriangle className="w-4 h-4 text-orange-400" />}
                        {status === "revoked" && <XCircle className="w-4 h-4 text-red-400" />}
                        <span className="text-slate-300 capitalize">{status}</span>
                      </div>
                      <span className="text-white font-medium">{count}</span>
                    </div>
                  ))}
                  {(!stats?.by_status || Object.keys(stats.by_status).length === 0) && (
                    <p className="text-slate-500 text-center py-4">No data available</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
